/**
 * Navbar.js
 *
 * Transparent → frosted-glass on scroll, with:
 * - Height shrink via CSS custom property
 * - Logo scale
 * - Backdrop blur + shadow
 * - Mobile menu toggle with ARIA state management
 */

import { throttle } from '../utilities/Utilities.js';

const SCROLL_THRESHOLD = 60; // px before navbar transforms
const SCROLLED_CLASS = 'scrolled';

export default class Navbar {
  constructor() {
    this.navbar      = document.getElementById('navbar');
    this.menuToggle  = document.getElementById('menu-toggle');
    this.mobileMenu  = document.getElementById('mobile-menu');
    this.menuIconOpen  = document.getElementById('menu-icon-open');
    this.menuIconClose = document.getElementById('menu-icon-close');

    // Search
    this.searchModal   = document.getElementById('search-modal');
    this.searchTrigger = document.getElementById('search-trigger');
    this.searchClose   = document.getElementById('search-close');
    this.searchBackdrop = document.getElementById('search-backdrop');
    this.searchInput   = document.getElementById('search-input');

    // Language dropdown
    this.langWrapper  = document.getElementById('lang-wrapper');
    this.langTrigger  = document.getElementById('lang-trigger');
    this.langDropdown = document.getElementById('lang-dropdown');
    this.langLabel    = document.getElementById('lang-label');
    this.langChevron  = document.getElementById('lang-chevron');

    // Profile dropdown
    this.profileWrapper  = document.getElementById('profile-wrapper');
    this.profileTrigger  = document.getElementById('profile-trigger');
    this.profileDropdown = document.getElementById('profile-dropdown');
    this.profileChevron  = document.getElementById('profile-chevron');

    if (!this.navbar) return;

    this._bindScroll();
    this._bindMobileMenu();
    this._bindSearch();
    this._bindLangDropdown();
    this._bindProfileDropdown();
    this._bindKeyboard();
    this._setYear();

    this._onScroll();
  }

  // ─── Scroll behaviour ──────────────────────────────────────────────────────

  _onScroll = () => {
    const isScrolled = window.scrollY > SCROLL_THRESHOLD;
    this.navbar.classList.toggle(SCROLLED_CLASS, isScrolled);
  };

  _bindScroll() {
    window.addEventListener('scroll', throttle(this._onScroll, 80), { passive: true });
  }

  // ─── Mobile menu ────────────────────────────────────────────────────────────

  _bindMobileMenu() {
    if (!this.menuToggle || !this.mobileMenu) return;

    this.menuToggle.addEventListener('click', () => {
      this._toggleMobileMenu();
    });

    // Close when clicking a nav link inside the mobile menu
    this.mobileMenu.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this._closeMobileMenu());
    });
  }

  _toggleMobileMenu() {
    const isOpen = this.menuToggle.getAttribute('aria-expanded') === 'true';
    isOpen ? this._closeMobileMenu() : this._openMobileMenu();
  }

  _openMobileMenu() {
    this.mobileMenu.classList.remove('hidden');
    this.mobileMenu.setAttribute('aria-hidden', 'false');
    this.menuToggle.setAttribute('aria-expanded', 'true');
    this.menuIconOpen?.classList.add('hidden');
    this.menuIconClose?.classList.remove('hidden');
    document.body.classList.add('menu-open');

    // Animate in
    requestAnimationFrame(() => {
      this.mobileMenu.style.opacity = '0';
      this.mobileMenu.style.transform = 'translateY(-8px)';
      this.mobileMenu.style.transition = 'opacity 250ms ease, transform 300ms cubic-bezier(0.19,1,0.22,1)';
      requestAnimationFrame(() => {
        this.mobileMenu.style.opacity = '1';
        this.mobileMenu.style.transform = 'translateY(0)';
      });
    });
  }

  _closeMobileMenu() {
    this.mobileMenu.style.opacity = '0';
    this.mobileMenu.style.transform = 'translateY(-8px)';

    const cleanup = () => {
      this.mobileMenu.classList.add('hidden');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.menuIconOpen?.classList.remove('hidden');
      this.menuIconClose?.classList.add('hidden');
      document.body.classList.remove('menu-open');
      this.mobileMenu.style.transition = '';
    };

    this.mobileMenu.addEventListener('transitionend', cleanup, { once: true });
  }

  // ─── Search modal ───────────────────────────────────────────────────────────

  _bindSearch() {
    this.searchTrigger?.addEventListener('click', (e) => {
      e.preventDefault();
      this._openSearch();
    });
    this.searchClose?.addEventListener('click', () => this._closeSearch());
    this.searchBackdrop?.addEventListener('click', () => this._closeSearch());
  }

  _openSearch() {
    this.searchModal.classList.add('is-open');
    this.searchModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.searchInput?.focus(), 50);
  }

  _closeSearch() {
    this.searchModal.classList.remove('is-open');
    this.searchModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  // ─── Language dropdown ──────────────────────────────────────────────────────

  _bindLangDropdown() {
    this.langTrigger?.addEventListener('click', (e) => {
      e.stopPropagation();
      this._toggleLangDropdown();
    });

    this.langDropdown?.querySelectorAll('.lang-option').forEach((btn) => {
      btn.addEventListener('click', () => {
        const lang = btn.dataset.lang;
        this.langLabel.textContent = btn.textContent.trim().replace(/^\S+\s*/, '').trim();
        this.langDropdown.querySelectorAll('.lang-option').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this._closeLangDropdown();
      });
    });

    document.addEventListener('click', (e) => {
      if (!this.langWrapper?.contains(e.target)) this._closeLangDropdown();
    });
  }

  _toggleLangDropdown() {
    const isOpen = this.langDropdown.classList.contains('is-open');
    isOpen ? this._closeLangDropdown() : this._openLangDropdown();
  }

  _openLangDropdown() {
    this.langDropdown.classList.add('is-open');
    this.langTrigger.setAttribute('aria-expanded', 'true');
    this.langChevron?.classList.add('rotate-180');
  }

  _closeLangDropdown() {
    this.langDropdown.classList.remove('is-open');
    this.langTrigger.setAttribute('aria-expanded', 'false');
    this.langChevron?.classList.remove('rotate-180');
  }

  // ─── Profile dropdown ───────────────────────────────────────────────────────

  _bindProfileDropdown() {
    this.profileTrigger?.addEventListener('click', (e) => {
      e.stopPropagation();
      this._toggleProfileDropdown();
    });

    this.profileDropdown?.querySelectorAll('.lang-option').forEach((link) => {
      link.addEventListener('click', () => this._closeProfileDropdown());
    });

    document.addEventListener('click', (e) => {
      if (!this.profileWrapper?.contains(e.target)) this._closeProfileDropdown();
    });
  }

  _toggleProfileDropdown() {
    const isOpen = this.profileDropdown?.classList.contains('is-open');
    isOpen ? this._closeProfileDropdown() : this._openProfileDropdown();
  }

  _openProfileDropdown() {
    this.profileDropdown?.classList.add('is-open');
    this.profileTrigger?.setAttribute('aria-expanded', 'true');
    this.profileChevron?.classList.add('rotate-180');
  }

  _closeProfileDropdown() {
    this.profileDropdown?.classList.remove('is-open');
    this.profileTrigger?.setAttribute('aria-expanded', 'false');
    this.profileChevron?.classList.remove('rotate-180');
  }

  // ─── Keyboard ───────────────────────────────────────────────────────────────

  _bindKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this._closeMobileMenu();
        this._closeSearch();
        this._closeLangDropdown();
        this._closeProfileDropdown();
      }
    });
  }

  // ─── Footer year ────────────────────────────────────────────────────────────

  _setYear() {
    const el = document.getElementById('current-year');
    if (el) el.textContent = new Date().getFullYear();
  }
}
